import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

import { getDropClassErrorMessage } from '../lib/drop-class-error-message';

export interface DropClassParams {
  classId: string;
  studentId: string;
  /** location Id in backend */
  studioId: string;
  droppingReason: string;
  shouldRefund?: boolean;
  isV2?: boolean;
}

export const dropClassAPI = async (body: DropClassParams) => {
  if (body.isV2) {
    const resp = await baseAPI.post<unknown>('/v2/class-drops', {
      classId: body.classId,
      studentId: body.studentId,
      droppingReason: body.droppingReason,
      shouldRefund: body.shouldRefund === true,
    });
    return resp.data;
  }

  const resp = await baseAPI.post<unknown>(
    `payment-processor/subscription/cancel`,
    {
      classId: body.classId,
      studentId: body.studentId,
      locationId: body.studioId,
      droppingReason: body.droppingReason,
    },
  );
  return resp.data;
};

export const useDropClassMutation = () => {
  return useMutation({
    mutationFn: async (body: DropClassParams) => {
      const response = await dropClassAPI(body);
      return response;
    },

    onSuccess: (_, body) => {
      void queryClient.invalidateQueries({
        queryKey: [KEYS.STUDENT_CLASS_HISTORY],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.STUDENTS, body.studentId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.STUDENTS, body.classId],
      });
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === KEYS.CLASSES,
      });
      // Dropping can cancel/adjust invoices → refresh Dashboard V2 AR
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ACCOUNTS_RECEIVABLE],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ACCOUNTS_RECEIVABLE_DUE_DATE_GROUPING],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ACCOUNTS_RECEIVABLE_STUDENT_GROUPING],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.STUDIO_DETAILS_ALL],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ENROLLMENT_TO_STUDENT_RATIO],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.TOTAL_ACTIVE_CLASSES],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ENROLLMENT_PERCENTAGE_OVER_LAST_MONTH],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.TOTAL_OUTSTANDING_REVENUE],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.TOTAL_SCHEDULED_UPCOMING_REVENUE],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.TOP_10_ENROLLED_STUDENTS],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.CLASS_PERFORMANCE_LOW_ENROLLMENT],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.CLASS_PERFORMANCE_NEAR_CAPACITY],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.NINETY_PERCENT_FULL_CLASSES],
      });
      toast.success('Class dropped successfully');
    },
    onError: (error) => {
      console.error('Failed to drop class', error);
      toast.error(getDropClassErrorMessage(error));
    },
  });
};
